import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Download, Menu, X, LogOut, LayoutDashboard } from "lucide-react";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";

const NAV = [
  { label: "Home", href: "#home" },
  { label: "Features", href: "#features" },
  { label: "Courses", href: "#courses" },
  { label: "Test Series", href: "#test-series" },
  { label: "Pricing", href: "#pricing" },
  { label: "Testimonials", href: "#testimonials" },
  { label: "FAQs", href: "#faqs" },
  { label: "Contact", href: "#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-slate-200/70 shadow-[0_4px_20px_-12px_rgba(15,23,42,0.15)]"
          : "bg-white/50 backdrop-blur-md"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="shrink-0">
          <Logo />
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV.map((n) => (
            <a
              key={n.label}
              href={n.href}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="text-sm font-medium text-slate-700 hover:text-blue-600 transition-colors relative after:absolute after:left-0 after:-bottom-1 after:h-[2px] after:w-0 hover:after:w-full after:bg-blue-600 after:transition-all"
            >
              {n.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-2">
          {user ? (
            <>
              <Button
                variant="ghost"
                onClick={() => navigate("/dashboard")}
                data-testid="navbar-dashboard-btn"
                className="text-slate-700"
              >
                <LayoutDashboard className="h-4 w-4 mr-2" />
                Dashboard
              </Button>
              <Button
                onClick={logout}
                variant="outline"
                data-testid="navbar-logout-btn"
                className="border-slate-300"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="ghost"
                onClick={() => navigate("/login")}
                data-testid="navbar-login-btn"
                className="text-slate-700 hover:text-blue-700"
              >
                Login
              </Button>
              <Button
                onClick={() => navigate("/register")}
                data-testid="navbar-cta-download"
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-5 shadow-md shadow-blue-600/20"
              >
                <Download className="h-4 w-4 mr-2" />
                Get Started
              </Button>
            </>
          )}
        </div>

        <button
          className="lg:hidden p-2 rounded-md text-slate-700"
          onClick={() => setOpen((v) => !v)}
          data-testid="navbar-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-slate-200" data-testid="navbar-mobile-menu">
          <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col gap-1">
            {NAV.map((n) => (
              <a
                key={n.label}
                href={n.href}
                onClick={() => setOpen(false)}
                className="px-3 py-2 rounded-lg text-slate-700 hover:bg-blue-50 hover:text-blue-700 font-medium"
              >
                {n.label}
              </a>
            ))}
            <div className="mt-2 flex gap-2">
              {user ? (
                <>
                  <Button
                    onClick={() => { setOpen(false); navigate("/dashboard"); }}
                    className="flex-1 bg-blue-600 hover:bg-blue-700"
                    data-testid="navbar-mobile-dashboard"
                  >
                    Dashboard
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => { setOpen(false); logout(); }}
                    className="flex-1"
                    data-testid="navbar-mobile-logout"
                  >
                    Logout
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="outline"
                    onClick={() => { setOpen(false); navigate("/login"); }}
                    className="flex-1"
                    data-testid="navbar-mobile-login"
                  >
                    Login
                  </Button>
                  <Button
                    onClick={() => { setOpen(false); navigate("/register"); }}
                    className="flex-1 bg-blue-600 hover:bg-blue-700"
                    data-testid="navbar-mobile-register"
                  >
                    Get Started
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
